/**
 * THE MASTERS, ON THE SERVICE SIDE.
 *
 * Three questions get asked constantly while writing or checking a work order,
 * and until 14-09-2026 answering any of them meant leaving the module:
 *
 *   is this contractor GST registered, and what is his PAN?
 *       It decides whether OVAL charges him GST or owes it under reverse
 *       charge, and the fourth character of the PAN decides whether TDS is 1%
 *       or 2%. Both are worked out automatically on the order - but the buyer
 *       negotiating on the telephone wants to know BEFORE he types it.
 *
 *   who is the Project Manager of that site?
 *       Stage 2 of every work order is his. "Why has this not moved" is
 *       answered by knowing whose it is.
 *
 *   is it SFT or SQFT, CUM or CFT?
 *       The UOM master says SFT and has no LS. Guessing is how one site
 *       measures in SQFT, another in SFT, and no report can add them up.
 *
 * READ ONLY, and deliberately so. The vendor master is maintained by
 * Procurement on the Materials side and the sites master by the administrator;
 * a second editor here would be two screens that have to agree forever. This
 * one shows, and says where to go to change.
 *
 * It widens nothing. sites, vendors and uoms are readable by every signed-in
 * user already (masters_read_sites, masters_read_vendors, uom_read, all
 * `using (true)` since go-live). What was missing was a door on this side.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Building2, FileText, HardHat, Landmark, Plus, Ruler, ShieldCheck, Trash2, Users,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { usePeople, useQuery, useSites, useVendors } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import {
  Card, EmptyState, Input, Loading, SearchInput, Textarea, Th, sortRows, useSort,
  useToast,
} from '@/components/ui'
import { VendorPapersCard } from '@/components/docs/shared'
import { serviceCan } from '../nav'

type Tab = 'contractors' | 'sites' | 'uoms' | 'terms'

const TABS: { key: Tab; label: string; icon: typeof Users }[] = [
  { key: 'contractors', label: 'Contractors', icon: HardHat },
  { key: 'sites', label: 'Sites', icon: Building2 },
  { key: 'uoms', label: 'Units of measure', icon: Ruler },
  { key: 'terms', label: 'Terms & conditions', icon: FileText },
]

/** The 4th character of a PAN is the holder's status. Same rule as the order. */
function tdsFromPan(pan?: string | null): number | null {
  if (!pan) return null
  const p = pan.trim().toUpperCase()
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(p)) return null
  return 'PH'.includes(p[3]) ? 1 : 2
}

export default function ServiceMasters() {
  const [tab, setTab] = useState<Tab>('contractors')

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Masters</h1>
        <p className="text-sm text-slate-500">
          Contractors, sites and units — what a work order is built out of.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={tab === t.key
                    ? 'flex items-center gap-1.5 border-b-2 border-amber-500 px-3 py-2 text-sm font-semibold text-amber-900'
                    : 'flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-800'}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'contractors' && <Contractors />}
      {tab === 'sites' && <Sites />}
      {tab === 'uoms' && <Units />}
      {tab === 'terms' && <Terms />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// CONTRACTORS
// ---------------------------------------------------------------------------
function Contractors() {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const { sort, toggle } = useSort('company_name', 'asc')
  const { data: vendors, loading } = useVendors()

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase()
    const pool = (vendors as any[]).filter((v) => !v.is_placeholder)
    return sortRows(pool.filter((v) => !t
      || [v.company_name, v.gstin, v.pan, v.state_name, v.contact_person, v.phone]
        .some((x) => String(x ?? '').toLowerCase().includes(t))), sort, {})
  }, [vendors, term, sort])

  if (loading) return <Loading label="Reading the contractor list…" />

  const unregistered = shown.filter((v: any) => !v.is_gst_registered).length
  const noPan = shown.filter((v: any) => !tdsFromPan(v.pan)).length

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Landmark className="mr-1 inline h-3.5 w-3.5" />
        <strong>{unregistered}</strong> of these {shown.length} are <strong>not GST
        registered</strong>, so OVAL pays the 18% to the government under reverse charge and
        deducts nothing from their bills.{' '}
        {noPan > 0 && <><strong>{noPan}</strong> have no usable PAN on file, which means TDS
        at 20% until somebody gets one. </>}
        Contractors are added and corrected on <strong>Material Procurement → Master
        Data → Vendors</strong>; this is the same list, read here.
      </div>

      <Card title={null}>
        <SearchInput value={term} onChange={setTerm} className="mb-3"
                     placeholder="Contractor, GSTIN, PAN or state…" />
        {shown.length === 0 ? (
          <EmptyState icon={<HardHat className="h-8 w-8" />} title="Nothing matches" />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="company_name" sort={sort} onSort={toggle}>Contractor</Th>
                  <Th id="state_name" sort={sort} onSort={toggle}>State</Th>
                  <th>GSTIN</th>
                  <th>GST</th>
                  <th>PAN</th>
                  <th className="num">TDS</th>
                  <th>Papers</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((v: any) => {
                  const tds = tdsFromPan(v.pan)
                  return (
                    <tr key={v.id}>
                      <td className="max-w-[220px]">
                        <span className="font-medium">{v.company_name}</span>
                        <span className="block text-[11px] text-slate-400">{v.id}</span>
                      </td>
                      <td className="whitespace-nowrap text-xs">{v.state_name ?? '—'}</td>
                      <td className="whitespace-nowrap font-mono text-xs">{v.gstin ?? '—'}</td>
                      <td>
                        {v.is_gst_registered
                          ? <span className="pill-green">registered</span>
                          : <span className="pill-amber">reverse charge</span>}
                      </td>
                      <td className="whitespace-nowrap font-mono text-xs">{v.pan ?? '—'}</td>
                      <td className="num whitespace-nowrap">
                        {tds === null
                          ? <span className="text-red-600" title="No valid PAN — TDS would be 20%">20%</span>
                          : <>{tds}% <span className="text-[10px] text-slate-400">s.393(1)</span></>}
                      </td>
                      <td>
                        <button className="btn-ghost btn-sm"
                                onClick={() => setOpen(open === v.id ? null : v.id)}>
                          {open === v.id ? 'Hide' : 'Show'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* The vendor's own papers, under exactly the policy they have always
          been under. Renders nothing for a role that may not open them. */}
      {open && (
        <VendorPapersCard
          vendorId={open}
          vendorName={shown.find((v: any) => v.id === open)?.company_name}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// SITES
// ---------------------------------------------------------------------------
function Sites() {
  const { profile } = useAuth()
  const people = usePeople()
  const [term, setTerm] = useState('')
  const { sort, toggle } = useSort('site_name', 'asc')
  const { data: sites, loading } = useSites()

  // Who is at each site. One query for all of them rather than one per row.
  const { data: staff } = useQuery<any>('service_master_staff',
    () => supabase.from('profiles')
      .select('id, full_name, role, site_id, is_active').eq('is_active', true),
    [], { cacheable: false })

  const atSite = useMemo(() => {
    const m: Record<string, { eng: string[]; rcm: string[] }> = {}
    for (const p of staff as any[]) {
      if (!p.site_id) continue
      const e = (m[p.site_id] ??= { eng: [], rcm: [] })
      if (p.role === 'site_engineer') e.eng.push(p.full_name)
      if (p.role === 'site_construction_manager') e.rcm.push(p.full_name)
    }
    return m
  }, [staff])

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase()
    return sortRows((sites as any[]).filter((s) => !t
      || [s.site_name, s.site_code, s.id, s.state_name]
        .some((x) => String(x ?? '').toLowerCase().includes(t))), sort, {})
  }, [sites, term, sort])

  if (loading) return <Loading label="Reading the sites…" />

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
        <strong>Stage 2 of every work order belongs to the Project Manager named
        here</strong> — not to whichever manager happens to be free. A site with no manager
        named cannot move past stage 2, and an administrator has to set one on{' '}
        <strong>Material Procurement → Master Data → Sites</strong>.
      </div>

      <Card title={null}>
        <SearchInput value={term} onChange={setTerm} className="mb-3"
                     placeholder="Site, code or state…" />
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <Th id="site_code" sort={sort} onSort={toggle}>Code</Th>
                <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                <Th id="state_name" sort={sort} onSort={toggle}>State</Th>
                <th>Project Manager</th>
                <th>Site Engineer</th>
                <th>Resident Construction Manager</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((s: any) => {
                const here = atSite[s.id] ?? { eng: [], rcm: [] }
                const mine = s.project_manager_id === profile?.id
                return (
                  <tr key={s.id} className={mine ? 'bg-amber-50/60' : undefined}>
                    <td className="whitespace-nowrap font-mono text-xs">{s.site_code ?? '—'}</td>
                    <td className="max-w-[240px]">
                      {s.site_name}
                      {mine && <span className="pill-amber ml-1">yours</span>}
                      {!s.is_active && <span className="pill-grey ml-1">closed</span>}
                    </td>
                    <td className="whitespace-nowrap text-xs">{s.state_name ?? '—'}</td>
                    <td className="max-w-[160px] truncate text-xs">
                      {s.project_manager_id
                        ? people.name({ id: s.project_manager_id })
                        : <span className="font-semibold text-red-600">nobody named</span>}
                    </td>
                    <td className="max-w-[160px] truncate text-xs">
                      {here.eng.join(', ') || '—'}
                    </td>
                    <td className="max-w-[180px] truncate text-xs">
                      {here.rcm.join(', ') || '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// UNITS
// ---------------------------------------------------------------------------
function Units() {
  const [term, setTerm] = useState('')
  const { data: uoms, loading } = useQuery<any>('uoms',
    () => supabase.from('uoms').select('*').eq('is_active', true)
      .order('uom_class').order('sort_order').order('code'),
    [], { cacheable: false, idField: 'code' })

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase()
    return (uoms as any[]).filter((u) => !t
      || [u.code, u.description, u.uom_class].some((x) => String(x ?? '').toLowerCase().includes(t)))
  }, [uoms, term])

  const classes = useMemo(
    () => [...new Set(shown.map((u: any) => u.uom_class ?? 'OTHER'))],
    [shown])

  if (loading) return <Loading label="Reading the units…" />

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Ruler className="mr-1 inline h-3.5 w-3.5" />
        The same {(uoms as any[]).length} codes the material side uses. Note it says{' '}
        <strong>SFT</strong>, not SQFT, and that there is <strong>no LS</strong> — a lumpsum
        line is priced as a milestone, not measured in a unit. A code that is genuinely
        missing is added on <strong>Material Procurement → Master Data → UOMs</strong>, never
        typed into a work order.
      </div>

      <Card title={null}>
        <SearchInput value={term} onChange={setTerm} className="mb-3"
                     placeholder="Code or description…" />
        {shown.length === 0 ? (
          <EmptyState icon={<Ruler className="h-8 w-8" />} title="Nothing matches" />
        ) : (
          <div className="space-y-4">
            {classes.map((c) => (
              <div key={String(c)}>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {String(c).toLowerCase()}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {shown.filter((u: any) => (u.uom_class ?? 'OTHER') === c).map((u: any) => (
                    <span key={u.code}
                          className="inline-flex items-baseline gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
                      <span className="font-mono font-semibold text-slate-800">{u.code}</span>
                      <span className="text-xs text-slate-500">{u.description}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}


// ---------------------------------------------------------------------------
// TERMS AND CONDITIONS — the standard clauses, per pricing basis
//
// The one master on this screen that is EDITABLE, because it is the one that
// belongs to this side of the house. Procurement Managers, Project Managers and
// the Project Head maintain it; everybody else reads it.
//
// Editing a clause here changes what the NEXT work order picks up. It never
// touches an order already written — each one keeps its own frozen copy, which
// is the whole reason wo_terms exists as a separate table.
// ---------------------------------------------------------------------------
const BASES: { key: string; label: string; blurb: string }[] = [
  { key: 'COMMON', label: 'General',
    blurb: 'On every contract, whatever it is priced on.' },
  { key: 'MEASURED_ITEM', label: 'Measured item (BOQ)',
    blurb: 'Paid on measured quantity against the Schedule of Rates.' },
  { key: 'AREA_RATE', label: 'Area rate',
    blurb: 'One rate over a stated area — roof, built-up, carpet.' },
  { key: 'LUMPSUM', label: 'Lumpsum',
    blurb: 'A fixed sum released on named milestones.' },
  { key: 'DAY_RATE', label: 'Day rate',
    blurb: 'Per head per day, billed off a signed attendance sheet.' },
]

interface TermRow {
  id: number; basis: string; clause_no: number; clause_text: string; is_active: boolean
}

function Terms() {
  const { profile } = useAuth()
  const toast = useToast()
  const [basis, setBasis] = useState('COMMON')
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<Record<number, string>>({})

  const canEdit = serviceCan(profile?.role, 'manageTerms')

  const { data: rows, loading, refresh } = useQuery<TermRow>(
    'wo_term_templates',
    () => supabase.from('wo_term_templates').select('*').order('basis').order('clause_no'),
    [], { cacheable: false })

  const mine = useMemo(
    () => (rows as TermRow[]).filter((r) => r.basis === basis)
      .sort((a, b) => a.clause_no - b.clause_no),
    [rows, basis])

  useEffect(() => {
    setDraft(Object.fromEntries((rows as TermRow[]).map((r) => [r.id, r.clause_text])))
  }, [rows])

  async function save(r: TermRow) {
    const text = (draft[r.id] ?? '').trim()
    if (!text || text === r.clause_text) return
    setBusy(true)
    try {
      const { error } = await supabase.from('wo_term_templates')
        .update({ clause_text: text, updated_by: profile?.id, updated_at: new Date().toISOString() })
        .eq('id', r.id)
      if (error) throw new Error(error.message)
      toast.success('Clause updated. It applies to the next order raised, not to any already written.')
      refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save that clause')
    } finally { setBusy(false) }
  }

  async function add() {
    setBusy(true)
    try {
      const next = mine.length ? Math.max(...mine.map((r) => r.clause_no)) + 10 : 10
      const { error } = await supabase.from('wo_term_templates').insert({
        basis, clause_no: next, clause_text: 'New clause — type it here.',
        updated_by: profile?.id,
      })
      if (error) throw new Error(error.message)
      refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add a clause')
    } finally { setBusy(false) }
  }

  async function retire(r: TermRow) {
    if (!window.confirm(
      `Retire clause ${r.clause_no}?\n\nIt stops appearing on new orders. Every order `
      + 'already written keeps it, because it is part of a contract.')) return
    setBusy(true)
    try {
      const { error } = await supabase.from('wo_term_templates')
        .update({ is_active: !r.is_active }).eq('id', r.id)
      if (error) throw new Error(error.message)
      refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change that clause')
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the standard clauses…" />

  const here = BASES.find((b) => b.key === basis)

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <FileText className="mr-1 inline h-3.5 w-3.5" />
        A work order takes the <strong>General</strong> set plus one set per pricing basis its
        lines actually use, and then keeps <strong>its own copy</strong>. Editing a clause here
        changes what the <strong>next</strong> order picks up — it never rewrites a contract
        already signed.{' '}
        {canEdit
          ? 'Click into a clause to edit it; it saves when you click away.'
          : 'Procurement, the Project Managers and the Project Head maintain these; you are reading them.'}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {BASES.map((b) => {
          const count = (rows as TermRow[]).filter((r) => r.basis === b.key && r.is_active).length
          return (
            <button key={b.key} onClick={() => setBasis(b.key)}
                    className={basis === b.key
                      ? 'rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900'
                      : 'rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50'}>
              {b.label} <span className="ml-1 text-xs text-slate-400">{count}</span>
            </button>
          )
        })}
      </div>

      <Card title={here?.label ?? basis} subtitle={here?.blurb}
            actions={canEdit ? (
              <button className="btn-secondary btn-sm" disabled={busy} onClick={() => void add()}>
                <Plus className="h-4 w-4" /> Add a clause
              </button>
            ) : undefined}>
        {mine.length === 0 ? (
          <EmptyState icon={<FileText className="h-8 w-8" />} title="No clauses in this set" />
        ) : (
          <ol className="space-y-2">
            {mine.map((r) => (
              <li key={r.id}
                  className={r.is_active ? 'flex gap-3' : 'flex gap-3 opacity-50'}>
                <span className="w-8 shrink-0 pt-2 text-right text-sm font-semibold tabular-nums text-slate-400">
                  {r.clause_no}
                </span>
                <div className="min-w-0 flex-1">
                  {canEdit && r.is_active ? (
                    <Textarea
                      rows={Math.max(2, Math.ceil((draft[r.id] ?? '').length / 110))}
                      value={draft[r.id] ?? ''}
                      onChange={(e) => setDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                      onBlur={() => void save(r)} />
                  ) : (
                    <p className="py-1.5 text-sm leading-relaxed text-slate-700">
                      {r.clause_text}
                      {!r.is_active && <span className="pill-grey ml-1">retired</span>}
                    </p>
                  )}
                </div>
                {canEdit && (
                  <button className="btn-ghost btn-sm mt-1 shrink-0 text-slate-500"
                          disabled={busy} onClick={() => void retire(r)}
                          title={r.is_active
                            ? 'Stop offering this clause on new orders'
                            : 'Offer it again'}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  )
}
